import { useCmrServiceCmrDirectionsList } from '@insurance/api/queries'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { getFormatCurrency } from '@shared/utils'
import { DescriptionsProps, Divider, Flex, Form } from 'antd'
import { useTranslation } from 'react-i18next'

import { DetailsWrapper } from '../DetailsWrapper'
import { useCmrInsuranceValues } from './CmrInsuranceContext'
import { useStyles } from './styles'
import { formProps } from './utils'

type ExtraDescriptions = (Required<DescriptionsProps>['items'][number] & {
  hidden?: boolean
})[]

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { isEditable, order } = useCmrInsuranceValues()

  const {
    priceKey,
    validFromProps,
    periodProps,
    totalAmountProps,
    perCaseAmountProps,
    generalCargoProps,
    highRiskCargoProps,
    employeeErrorsProps,
    additionalExpensesProps,
    installmentsProps,
    contractorActivityTypeProps,
    destinationsProps,
    vehiclesProps,
    contractorHeadProps,
    contractorContactRoleProps,
    contractorTransportLicense,
    contractorCompanyNameProps,
    effectiveDateProps,
    modifyReasonProps,
    currencyAmountProps
  } = formProps(t)

  const price = Form.useWatch(priceKey.name, { preserve: true })
  const validFrom = Form.useWatch(validFromProps.name, { preserve: true })
  const effectiveDate = Form.useWatch(effectiveDateProps.name, {
    preserve: true
  })
  const period = Form.useWatch(periodProps.name, { preserve: true })
  const totalAmount = Form.useWatch(totalAmountProps.name, { preserve: true })
  const currencyAmount = Form.useWatch(currencyAmountProps.name, {
    preserve: true
  })
  const perCaseAmount = Form.useWatch(perCaseAmountProps.name, {
    preserve: true
  })
  const generalCargo = Form.useWatch(generalCargoProps.name, { preserve: true })
  const highRiskCargo = Form.useWatch(highRiskCargoProps.name, {
    preserve: true
  })
  const employeeErrors = Form.useWatch(employeeErrorsProps.name, {
    preserve: true
  })
  const additionalExpenses = Form.useWatch(additionalExpensesProps.name, {
    preserve: true
  })
  const installments = Form.useWatch(installmentsProps.name, { preserve: true })
  const activityType = Form.useWatch(contractorActivityTypeProps.name, {
    preserve: true
  })
  const destinations = Form.useWatch<string[]>(destinationsProps.name, {
    preserve: true
  })
  const vehicles = Form.useWatch<unknown[]>(vehiclesProps.name, {
    preserve: true
  })
  const contractorHead = Form.useWatch(contractorHeadProps.name, {
    preserve: true
  })
  const contractorContactRole = Form.useWatch(contractorContactRoleProps.name, {
    preserve: true
  })
  const contractorTransportLicenseValue = Form.useWatch(
    contractorTransportLicense.name,
    { preserve: true }
  )
  const contractorCompanyName = Form.useWatch(contractorCompanyNameProps.name, {
    preserve: true
  })
  const modifyReason = Form.useWatch(modifyReasonProps.name, {
    preserve: true
  })

  const { data: cmrDirections } = useCmrServiceCmrDirectionsList({})

  const items: ExtraDescriptions = [
    {
      label: validFromProps.label,
      children: (
        <DateTimeCell
          date={validFrom}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: isEditable
    },
    {
      label: effectiveDateProps.label,
      children: (
        <DateTimeCell
          dateProps={{ className: styles.date }}
          date={effectiveDate}
          hiddenTime
        />
      ),
      hidden: !isEditable
    },
    {
      label: periodProps.label,
      children: (
        <ConditionalText condition={!!period} className={styles.text}>
          {period ? t(`m${period}`) : ''}
        </ConditionalText>
      )
    },
    {
      label: totalAmountProps.label,
      children: getFormatCurrency({
        amount: totalAmount,
        currency: currencyAmount
      }),
      hidden: !totalAmount
    },
    {
      label: perCaseAmountProps.label,
      children: getFormatCurrency({
        amount: perCaseAmount,
        currency: currencyAmount
      }),
      hidden: !perCaseAmount
    },
    {
      label: generalCargoProps.label,
      children: generalCargo ? t('yes') : t('no'),
      hidden: typeof generalCargo !== 'boolean'
    },
    {
      label: highRiskCargoProps.label,
      children: highRiskCargo ? t('yes') : t('no'),
      hidden: typeof highRiskCargo !== 'boolean'
    },
    {
      label: employeeErrorsProps.label,
      children: employeeErrors ? t('yes') : t('no'),
      hidden: typeof employeeErrors !== 'boolean'
    },
    {
      label: additionalExpensesProps.label,
      children: additionalExpenses ? t('yes') : t('no'),
      hidden: typeof additionalExpenses !== 'boolean'
    },
    {
      label: installmentsProps.label,
      children: (
        <ConditionalText condition={!!installments} className={styles.text}>
          {installments ? t(`installment_${installments}`) : ''}
        </ConditionalText>
      ),
      hidden: !installments
    },
    {
      label: contractorActivityTypeProps.label,
      children: (
        <ConditionalText condition={!!activityType} className={styles.text}>
          {activityType ? t(activityType) : ''}
        </ConditionalText>
      ),
      hidden: !activityType
    },
    {
      label: destinationsProps.label,
      children: destinations
        ?.map(
          (dest) =>
            cmrDirections?.find(({ code }) => code === dest)?.name || dest
        )
        .join(', '),
      hidden: !destinations?.length
    },
    {
      label: vehiclesProps.label,
      children: vehicles?.length ?? 0,
      hidden: !vehicles?.length
    },
    {
      label: contractorHeadProps.label,
      children: contractorHead,
      hidden: !contractorHead
    },
    {
      label: contractorContactRoleProps.label,
      children: contractorContactRole,
      hidden: !contractorContactRole
    },
    {
      label: contractorTransportLicense.label,
      children: contractorTransportLicenseValue,
      hidden: !contractorTransportLicenseValue
    },
    {
      label: contractorCompanyNameProps.label,
      children: contractorCompanyName,
      hidden: !contractorCompanyName
    },
    {
      label: modifyReasonProps.label,
      children: (
        <ConditionalText condition={!!modifyReason} className={styles.text}>
          {modifyReason}
        </ConditionalText>
      ),
      hidden: !isEditable
    }
  ].filter(({ hidden }) => !hidden)

  const orderPrice = {
    amount: order?.full_price ? Number(order.full_price) : 0,
    currency: order?.currency
  }

  const isMoreThanOrderPrice = price?.amount - orderPrice?.amount > 0

  return (
    <DetailsWrapper items={items}>
      {price && !isEditable && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong size={18}>
              {t('total_to_be_paid')}
            </Text>
            <Text strong size={18} className="text-wrap-nowrap">
              {getFormatCurrency(price)}
            </Text>
          </Flex>

          <Divider />
        </>
      )}

      {isEditable && (
        <>
          {orderPrice && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('policy_amount_paid')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(orderPrice)}
              </Text>
            </Flex>
          )}
          {price && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('new_amount')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(price)}
              </Text>
            </Flex>
          )}
          {orderPrice && price && (
            <Flex align="center" justify="space-between">
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
              >
                {isMoreThanOrderPrice
                  ? t('amount_to_be_paid')
                  : t('amount_to_be_returned')}
              </Text>
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
                className="text-wrap-nowrap"
              >
                {getFormatCurrency({
                  amount: price.amount - orderPrice.amount,
                  currency: price.currency
                })}
              </Text>
            </Flex>
          )}

          <Divider />
        </>
      )}
    </DetailsWrapper>
  )
}
